import config from "../config";
import { encryptPartnerFiles } from "./dataEncryption";

/*
  The files a customer shares with their partner: the rules, without the UI.

  Kept apart from the component because these are the parts worth being sure
  about -- what may be uploaded, how a folder is counted, how much room is
  left -- and a rule buried in a render function is a rule nobody can check.

  The bytes themselves go through UploadFiles like every other upload,
  encrypted under the customer's key before they leave the browser. Nothing
  here touches a key; it only decides what is allowed and how it reads.
*/

/*
  Store the list.

  Its own endpoint, not /data/update: that route rewrites the whole
  document from the browser's copy, so any screen that saves -- an asset,
  a beneficiary, the advisor modal -- posts its own snapshot of
  everything. A snapshot taken before an upload finished carries an empty
  list and erases a file already on disk, and the sweeper then deletes the
  bytes. That is exactly what was happening: the first save kept the file,
  the next one wiped it.

  The metadata is encrypted here, under the customer's own key. The ids go
  in the clear beside it because the server needs them -- to authorise the
  partner's download, and to know the file is not an orphan -- and an id
  is 32 random characters that describe nothing.
*/
export const saveSharedFiles = async (files, private_key) => {
  const list = Array.isArray(files) ? files : [];
  const body = new URLSearchParams({
    files: await encryptPartnerFiles(private_key, list),
    ids: JSON.stringify(list.map((file) => file?.id).filter(Boolean)),
  });
  const res = await fetch(config.base_url + "user/shared-files", {
    method: "POST",
    mode: "cors",
    credentials: "include",
    headers: {
      Accept: "application/json, text/plain, */*",
      "Content-type": "application/x-www-form-urlencoded",
    },
    body: body.toString(),
  });
  if (res.status !== 200) throw new Error(`Could not save the list (${res.status})`);
  return res.json();
};

// What the design states, and what the server is configured for.
export const MAX_FILES = 60;
export const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB
/*
  The account's whole allowance, shared with every other upload -- an
  asset's documents, a beneficiary's. The same figure the Subscription tab
  shows, which is where the customer will have seen it.
*/
export const STORAGE_CAPACITY = 1024 * 1024 * 1024; // 1GB

/*
  Extensions refused outright.

  Not a virus check -- it cannot be one. It is the narrower thing a browser
  can actually promise: that what lands in the customer's storage, and what
  their partner is later invited to download, is not a program. A document
  store that will accept an .exe is a delivery mechanism, and the person
  who would be handed it is the one the customer trusts most.

  Matched on the LAST extension, so `invoice.pdf.exe` is refused -- that
  being the whole point of the double extension.
*/
export const BLOCKED_EXTENSIONS = [
  "exe", "msi", "bat", "cmd", "com", "scr", "pif", "cpl", "msc", "hta",
  "vb", "vbs", "vbe", "js", "jse", "wsf", "wsh", "ps1", "psm1", "ps1xml",
  "reg", "lnk", "inf", "gadget", "application", "jar", "sh", "bash", "zsh",
  "csh", "ksh", "run", "bin", "out", "app", "dmg", "pkg", "deb", "rpm",
  "apk", "ipa", "dll", "so", "dylib", "sys", "drv", "efi", "iso", "img",
];

export const extensionOf = (name) => {
  const parts = String(name || "").split(".");
  return parts.length > 1 ? parts.pop().toLowerCase() : "";
};

export const isBlockedFile = (name) =>
  BLOCKED_EXTENSIONS.includes(extensionOf(name));

/*
  A size, as the customer reads it.

  The same shape the Subscription tab uses, so "121.21 KB of your 1 GB"
  matches what they have already seen there.
*/
export const readableSize = (bytes) => {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return "";
  if (n === 0) return "0 KB";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let value = n;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  // Whole numbers for bytes, two decimals above -- 9.56 MB, not 9.5581 MB.
  return `${i === 0 ? Math.round(value) : Number(value.toFixed(2))} ${units[i]}`;
};

/*
  What the Type column says.

  Read off the extension rather than the browser's media type: the
  extension is what the customer sees in their own file manager, and a
  browser hands back an empty type often enough that a column driven by it
  would be half blank.
*/
const TYPE_NAMES = {
  pdf: "PDF document",
  doc: "Microsoft Word Document", docx: "Microsoft Word Document",
  xls: "Microsoft Excel Spreadsheet", xlsx: "Microsoft Excel Spreadsheet",
  csv: "CSV file",
  ppt: "Microsoft PowerPoint", pptx: "Microsoft PowerPoint",
  txt: "Text Document", rtf: "Rich Text Document", md: "Text Document",
  jpg: "JPEG image", jpeg: "JPEG image", png: "PNG image", gif: "GIF image",
  webp: "WebP image", heic: "HEIC image", svg: "SVG image", bmp: "Bitmap image",
  tif: "TIFF image", tiff: "TIFF image",
  mp4: "MP4 file", mov: "QuickTime movie", avi: "AVI file", mkv: "MKV file",
  webm: "WebM file",
  mp3: "MP3 file", wav: "WAV file", flac: "FLAC file", m4a: "M4A file",
  aac: "AAC file", ogg: "OGG file",
  zip: "ZIP archive", rar: "RAR archive", "7z": "7Z archive",
  tar: "TAR archive", gz: "GZ archive",
};

/*
  Which icon a file gets.

  Grouped rather than one-per-extension: a reader scanning a list is
  looking for "the video" or "the spreadsheet", not for the difference
  between .mov and .mkv. The groups are the ones a file manager draws
  differently, and anything unrecognised falls back to a plain page rather
  than being guessed at.
*/
const CATEGORIES = {
  image: ["jpg", "jpeg", "png", "gif", "webp", "heic", "heif", "svg", "bmp",
          "tif", "tiff", "avif", "ico"],
  video: ["mp4", "mov", "avi", "mkv", "webm", "m4v", "mpg", "mpeg", "wmv",
          "flv", "3gp"],
  audio: ["mp3", "wav", "flac", "m4a", "aac", "ogg", "oga", "aiff", "wma",
          "opus"],
  pdf: ["pdf"],
  word: ["doc", "docx", "odt", "rtf", "pages"],
  excel: ["xls", "xlsx", "ods", "csv", "numbers"],
  powerpoint: ["ppt", "pptx", "odp", "key"],
  archive: ["zip", "rar", "7z", "tar", "gz", "bz2", "xz"],
  text: ["txt", "md", "log", "json", "xml", "yml", "yaml"],
};

export const categoryOf = (name) => {
  const ext = extensionOf(name);
  for (const [category, list] of Object.entries(CATEGORIES)) {
    if (list.includes(ext)) return category;
  }
  return "file";
};

export const FOLDER_TYPE = "File Folder";

export const typeLabel = (name) => {
  const ext = extensionOf(name);
  if (!ext) return "File";
  return TYPE_NAMES[ext] || `${ext.toUpperCase()} file`;
};

/*
  Where a dropped or picked file belongs.

  A folder picked with `webkitdirectory`, or dragged in, arrives as a flat
  list of files each carrying the path it had inside that folder. The path
  is kept so the table can show the folders back -- a customer who shared a
  folder of twelve things should see the folder, not twelve loose rows.

  The file's own name is stripped off the end; what is left is its folder.
*/
export const folderPathOf = (file) => {
  const relative = file?.webkitRelativePath || file?.relativePath || "";
  if (!relative) return "";
  const parts = relative.split("/").filter(Boolean);
  parts.pop();
  return parts.join("/");
};

/*
  Everything in a drop, folders included.

  `dataTransfer.files` does NOT recurse: drop a folder and it hands back a
  single zero-byte entry for the folder itself, which is why a dropped
  folder appeared to upload and contained nothing. The real files are only
  reachable through the entry API, one directory listing at a time.

  Each file comes back carrying the path it had inside the drop, under the
  same `relativePath` the folder picker calls `webkitRelativePath`, so both
  ways in produce the same thing.

  Depth-limited and breadth-limited on purpose: a dropped symlink loop or a
  node_modules would otherwise walk until the tab gave up, and 60 files is
  the cap anyway.
*/
const MAX_WALK_DEPTH = 12;
const MAX_WALK_ENTRIES = 2000;

const readEntries = (reader) =>
  new Promise((resolve) => {
    reader.readEntries(
      (batch) => resolve(batch),
      () => resolve([])
    );
  });

const fileOf = (entry) =>
  new Promise((resolve) => {
    entry.file(
      (file) => resolve(file),
      () => resolve(null)
    );
  });

export const filesFromDrop = async (dataTransfer) => {
  const items = Array.from(dataTransfer?.items || []);
  const entries = items
    .map((item) => (item.webkitGetAsEntry ? item.webkitGetAsEntry() : null))
    .filter(Boolean);

  /*
    No entry API -- an older browser, or a drag that carried no items. Fall
    back to the flat list, which at least handles loose files.
  */
  if (!entries.length) return Array.from(dataTransfer?.files || []);

  const out = [];
  const walk = async (entry, prefix, depth) => {
    if (out.length >= MAX_WALK_ENTRIES || depth > MAX_WALK_DEPTH) return;
    if (entry.isFile) {
      const file = await fileOf(entry);
      if (!file) return;
      // Stamped with where it sat, so the table can show the folder back.
      file.relativePath = prefix ? `${prefix}/${entry.name}` : entry.name;
      out.push(file);
      return;
    }
    if (!entry.isDirectory) return;
    const reader = entry.createReader();
    const here = prefix ? `${prefix}/${entry.name}` : entry.name;
    // readEntries returns a page at a time and signals the end with [].
    for (;;) {
      // eslint-disable-next-line no-await-in-loop
      const batch = await readEntries(reader);
      if (!batch.length) break;
      for (const child of batch) {
        // eslint-disable-next-line no-await-in-loop
        await walk(child, here, depth + 1);
      }
    }
  };

  for (const entry of entries) {
    // eslint-disable-next-line no-await-in-loop
    await walk(entry, "", 0);
  }
  return out;
};

/*
  The flat list, as a tree of folders and files.

  Folders are inferred from the paths rather than stored: there is no such
  thing here as an empty folder, because a folder only exists as the thing
  its files have in common. A folder's size is its files' sizes and its
  date is the newest of them, which is what a file manager shows.
*/
export const buildTree = (files) => {
  const root = { folders: new Map(), files: [] };
  for (const file of Array.isArray(files) ? files : []) {
    const parts = String(file?.path || "").split("/").filter(Boolean);
    let node = root;
    for (const part of parts) {
      if (!node.folders.has(part)) {
        node.folders.set(part, { folders: new Map(), files: [] });
      }
      node = node.folders.get(part);
    }
    node.files.push(file);
  }

  const totals = (node) => {
    let size = 0;
    let count = 0;
    let modified = 0;
    for (const file of node.files) {
      size += Number(file?.size) || 0;
      count += 1;
      modified = Math.max(modified, Number(file?.lastModified) || 0);
    }
    for (const child of node.folders.values()) {
      const sub = totals(child);
      size += sub.size;
      count += sub.count;
      modified = Math.max(modified, sub.modified);
    }
    return { size, count, modified };
  };

  const rows = (node, prefix) => {
    const out = [];
    // Folders first, then files, each alphabetical -- as a file manager lists.
    const names = [...node.folders.keys()].sort((a, b) => a.localeCompare(b));
    for (const name of names) {
      const child = node.folders.get(name);
      const sub = totals(child);
      out.push({
        kind: "folder",
        name,
        path: prefix ? `${prefix}/${name}` : name,
        size: sub.size,
        count: sub.count,
        lastModified: sub.modified,
        children: rows(child, prefix ? `${prefix}/${name}` : name),
      });
    }
    const sorted = [...node.files].sort((a, b) =>
      String(a?.name || "").localeCompare(String(b?.name || ""))
    );
    for (const file of sorted) out.push({ kind: "file", ...file });
    return out;
  };

  return rows(root, "");
};

/*
  Which of a chosen batch may be kept, and why the rest may not.

  Two kinds of refusal, answered differently, because the customer can do
  something different about each.

  A file that is a PROGRAM or is over the per-file limit is refused on its
  own and the rest still go. Those refusals come back as one entry per
  REASON carrying a count, not one per file. Dropping forty documents should not fail
  because one of them is an .exe, and removing that one is the whole of
  the fix.

  Running out of ROOM -- the 60-file cap or the storage allowance -- stops
  the batch outright. Taking the first sixty of a hundred and stopping
  leaves the customer with an arbitrary sixty and no way to know which;
  they cannot act on that, and they would have to work out what is missing
  by reading the list. Better to take none and say what the numbers are.

  Counted before anything is uploaded, so nothing is sent and then
  abandoned -- which also means no half-uploaded batch for the orphan
  sweeper to collect.

  The two are judged in that order: an .exe inside a dropped folder is
  thrown out first, so it never counts against the sixty.
*/
export const screenUpload = (incoming, { existing = 0, used = 0 } = {}) => {
  const list = Array.isArray(incoming) ? incoming : [];
  const rejected = [];
  const candidates = [];

  /*
    Refusals are TALLIED, not listed.

    A picked folder can be tens of thousands of files -- 74,032 in one
    report -- and most of a real one is scripts, so keeping an object per
    refusal meant tens of thousands of them for a message that only ever
    says a number. The name is kept while there is exactly one, because
    that is when naming it helps.
  */
  const tally = {};
  const note = (reason, name) => {
    const seen = tally[reason] || (tally[reason] = { reason, count: 0, name: "" });
    seen.count += 1;
    seen.name = seen.count === 1 ? name : "";
  };

  for (const file of list) {
    const name = file?.name || "";
    if (isBlockedFile(name)) {
      note("blocked", name);
      continue;
    }
    if (Number(file?.size) > MAX_FILE_SIZE) {
      note("too_large", name);
      continue;
    }
    candidates.push(file);
  }
  rejected.push(...Object.values(tally));

  if (!candidates.length) return { accepted: [], rejected, refused: null };

  const total = existing + candidates.length;
  if (total > MAX_FILES) {
    return {
      accepted: [],
      rejected,
      refused: {
        reason: "too_many",
        adding: candidates.length,
        existing,
        total,
        max: MAX_FILES,
        // What they can actually add, which is the number worth telling them.
        room: Math.max(0, MAX_FILES - existing),
      },
    };
  }

  const bytes = candidates.reduce((n, f) => n + (Number(f?.size) || 0), 0);
  if (used + bytes > STORAGE_CAPACITY) {
    return {
      accepted: [],
      rejected,
      refused: {
        reason: "no_storage",
        needed: bytes,
        free: Math.max(0, STORAGE_CAPACITY - used),
      },
    };
  }

  return { accepted: candidates, rejected, refused: null };
};
